/**
 * Service Apollo por domínio para CategoriaLiga — mirror de `categoriaTorneioService.ts`.
 *
 * PRPs/plans/categoria-liga-phase-3-frontend.plan.md — Task 2 (Satisfies AC-A1, AC-A5,
 * AC-A6). Toda operação GraphQL de CategoriaLiga fica encapsulada aqui — nenhum `gql`
 * em componente/page/hook (anti-pattern "GraphQL inline em componentes ou pages").
 * Contrato exato de `inplay-back/src/main/resources/graphql/categoriaLiga.graphqls`.
 */
import { gql } from '@apollo/client';
import { apolloClient } from '../apollo/client';
import {
  ModeloDisputa,
  TipoPontuacao,
  StatusCategoriaTorneio,
  GeneroCategoria,
} from '../types/categoriaTorneio';
import type { CategoriaLiga, CategoriaLigaInput, CategoriaTorneioVinculada } from '../types/categoriaLiga';

export {
  ModeloDisputa,
  TipoPontuacao,
  StatusCategoriaTorneio,
  GeneroCategoria,
};

const CATEGORIA_LIGA_FIELDS = `
  id
  nome
  descricao
  genero
  nivel
  combinaveis {
    id
    nome
    genero
    nivel
  }
  idadeInicial
  idadeFinal
  idadeCombinada
  modeloDisputa
  tipoPontuacao
  numeroSets
  gamesPorSets
  tiebreaks
  quantidadeGrupos
  classificadosPorGrupo
  tamanhoGrupo
  precoInscricao
  numeroAtletas
  criadoEm
  atualizadoEm
  liga {
    id
    nome
  }
`;

export const CATEGORIAS_LIGA = gql`
  query CategoriasLiga($ligaId: ID!) {
    categoriasLiga(ligaId: $ligaId) {
      ${CATEGORIA_LIGA_FIELDS}
    }
  }
`;

export const CATEGORIA_LIGA_POR_ID = gql`
  query CategoriaLigaPorId($id: ID!) {
    categoriaLigaPorId(id: $id) {
      ${CATEGORIA_LIGA_FIELDS}
    }
  }
`;

export const CATEGORIAS_TORNEIO_POR_CATEGORIA_LIGA = gql`
  query CategoriasTorneioPorCategoriaLiga($categoriaLigaId: ID!) {
    categoriasTorneioPorCategoriaLiga(categoriaLigaId: $categoriaLigaId) {
      id
      nome
      torneio {
        id
        nome
      }
    }
  }
`;

export const CRIAR_CATEGORIA_LIGA = gql`
  mutation CriarCategoriaLiga($input: CategoriaLigaInput!) {
    criarCategoriaLiga(input: $input) {
      ${CATEGORIA_LIGA_FIELDS}
    }
  }
`;

export const ATUALIZAR_CATEGORIA_LIGA = gql`
  mutation AtualizarCategoriaLiga($id: ID!, $input: CategoriaLigaInput!) {
    atualizarCategoriaLiga(id: $id, input: $input) {
      ${CATEGORIA_LIGA_FIELDS}
    }
  }
`;

export const DELETAR_CATEGORIA_LIGA = gql`
  mutation DeletarCategoriaLiga($id: ID!) {
    deletarCategoriaLiga(id: $id)
  }
`;

export const buscarCategoriasLiga = async (ligaId: string): Promise<CategoriaLiga[]> => {
  try {
    const { data } = await apolloClient.query<{ categoriasLiga: CategoriaLiga[] }>({
      query: CATEGORIAS_LIGA,
      variables: { ligaId },
      fetchPolicy: 'no-cache',
    });
    return data?.categoriasLiga ?? [];
  } catch (e) {
    throw new Error('Erro ao buscar categorias da liga');
  }
};

export const buscarCategoriaLigaPorId = async (id: string): Promise<CategoriaLiga> => {
  try {
    const { data } = await apolloClient.query<{ categoriaLigaPorId: CategoriaLiga | null }>({
      query: CATEGORIA_LIGA_POR_ID,
      variables: { id },
      fetchPolicy: 'no-cache',
    });
    if (!data?.categoriaLigaPorId) {
      throw new Error('Categoria da liga nao encontrada');
    }
    return data.categoriaLigaPorId;
  } catch (e) {
    throw new Error('Erro ao buscar categoria da liga');
  }
};

export const criarCategoriaLiga = async (input: CategoriaLigaInput): Promise<CategoriaLiga> => {
  try {
    const { data } = await apolloClient.mutate<{ criarCategoriaLiga: CategoriaLiga }>({
      mutation: CRIAR_CATEGORIA_LIGA,
      variables: { input },
    });
    if (!data?.criarCategoriaLiga) {
      throw new Error('Resposta invalida');
    }
    return data.criarCategoriaLiga;
  } catch (e) {
    throw new Error('Erro ao criar categoria da liga');
  }
};

export const atualizarCategoriaLiga = async (
  id: string,
  input: CategoriaLigaInput,
): Promise<CategoriaLiga> => {
  try {
    const sanitizedInput = Object.fromEntries(
      Object.entries(input).map(([key, value]) => [key, value === undefined ? null : value]),
    ) as CategoriaLigaInput;

    const { data } = await apolloClient.mutate<{ atualizarCategoriaLiga: CategoriaLiga }>({
      mutation: ATUALIZAR_CATEGORIA_LIGA,
      variables: { id, input: sanitizedInput },
    });
    if (!data?.atualizarCategoriaLiga) {
      throw new Error('Resposta invalida');
    }
    return data.atualizarCategoriaLiga;
  } catch (e) {
    throw new Error('Erro ao atualizar categoria da liga');
  }
};

export const deletarCategoriaLiga = async (id: string): Promise<boolean> => {
  try {
    const { data } = await apolloClient.mutate<{ deletarCategoriaLiga: boolean }>({
      mutation: DELETAR_CATEGORIA_LIGA,
      variables: { id },
    });
    return data?.deletarCategoriaLiga ?? false;
  } catch (e) {
    throw new Error('Erro ao deletar categoria da liga');
  }
};

export const buscarCategoriasTorneioPorCategoriaLiga = async (
  categoriaLigaId: string,
): Promise<CategoriaTorneioVinculada[]> => {
  try {
    const { data } = await apolloClient.query<{
      categoriasTorneioPorCategoriaLiga: CategoriaTorneioVinculada[];
    }>({
      query: CATEGORIAS_TORNEIO_POR_CATEGORIA_LIGA,
      variables: { categoriaLigaId },
      fetchPolicy: 'no-cache',
    });
    return data?.categoriasTorneioPorCategoriaLiga ?? [];
  } catch (e) {
    throw new Error('Erro ao buscar categorias de torneio vinculadas');
  }
};
